/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-lonely-if */
import React, { ReactElement, useContext, useEffect, useState, useMemo, useCallback } from 'react';

import { updateSurveyAPI, getByeonjeungResult, markMBTIUserCompletedAPI } from '@api/controller';
import { Button, Icon, Row, Spinner } from '@components/atoms';
import { currentEnv } from '@config';
import { UPDATE_SURVEY_ACTION } from '@constants/actions';
import { codeMap } from '@constants/code';
import {
  exceptYnSurveys,
  noYnSurveys,
  Page,
  requiredSurveys,
  requiredYnSurveys,
  SelectType,
  omitReqSurveyCdByIsrcSurveyCd,
  B2C_VERSION,
} from '@constants/enum';
import {
  CONFIRM,
  CONTINUE_SURVEY_BUTTON,
  INIT_SURVEY_BUTTON,
  NEXT_BUTTON,
  NEXT_TO_CONFIRM_BUTTON,
  PREV_BUTTON,
  REVIEW_NEXT_BUTTON,
  START,
  B2C_START,
  MODE_SELECT_BUTTON,
} from '@constants/text';
import PageIndexContext from '@containers/Context/PageIndexContext';
import { useReduxForm } from '@hooks/useReduxForm';
import { useRequest } from '@hooks/useRequest';
import { getSurveyObj, extractScodes } from '@utils/surveyUtils';
import _ from 'lodash';
import moment from 'moment';
import { uuid } from 'uuidv4';

export default function Footer({ data, postSurvey }: { data: any; postSurvey: any }): ReactElement {
  const debugMode = currentEnv === 'local';
  const { formData, setFormData } = useReduxForm('survey');
  const { formData: b2cFormData = {} } = useReduxForm('b2cform');
  const { formData: mbtiFlowFormData } = useReduxForm('mbtiFlow');
  const [isDisabled, setIsDisabled] = useState(true);
  const [isDisabledForB2CForm, setIsDisabledForB2CForm] = useState(true);
  const [isDisabledForB2CAddintionalSurvey, setIsDisabledForB2CAddintionalSurvey] = useState(true);
  const [isLoadingForB2CForm, setIsLoadingForB2CForm] = useState(false);
  const [isLoadingForB2CResult, setIsLoadingForB2CResult] = useState(false);
  const [isDisabledForMainCategory, setIsDisabledForMainCategory] = useState(true);
  const [isDisabledForSurvey, setIsDisabledForSurvey] = useState(true);
  const [isDisabledForOptionalSurvey, setIsDisabledForOptionalSurvey] = useState(true);
  const [isDisabledForReview, setIsDisabledForReview] = useState(true);
  const [isDisabledForConfirm, setIsDisabledForConfirm] = useState(true);
  const { pageIndex, setPageIndex } = useContext(PageIndexContext);
  const {
    requestForSingle: surveyUpdate,
    resData: { data: { updateSurvey: updatedData } } = { data: { updateSurvey: {} } },
    requestSuccess,
    error,
    paramsForRequest,
    setParamsForRequest,
  } = useRequest({
    reduxActionName: UPDATE_SURVEY_ACTION,
    controllerFunc: updateSurveyAPI,
  });

  const {
    _id,
    _version,
    surveyList,
    mainSurveyList,
    selectedMainSurveyCode,
    selectedInsuranceSurveyCode,
    selectedOptionalSurveyList,
    optionalSurveysSkipFlag,
    mainSymptom,
    serviceTermAgreement,
    personalTermAgreement,
    reviewRating,
    reviewComment,
    visitedSurvey = [],
    mode: surveyMode,
    surveyType,
    initFormData,
    initMainSurveyList,
    patient,
  } = formData ?? {};

  const startB2CSurvey = useCallback(async ({ paramsForRequest: pr, d, age, gender, name }) => {
    try {
      await setIsLoadingForB2CForm(true);
      await setParamsForRequest({ ...pr, completedDt: null, rawData: '{}' });
      await setFormData({ ..._.mapValues(d, () => undefined) });
      const id = `${moment().format('YYYYMMDD')}${uuid()}`;
      localStorage.setItem('survey-id', id);
      localStorage.setItem('survey-name', name);
      localStorage.setItem('survey-version', B2C_VERSION);
      await postSurvey({
        id,
        surveyType: 'B2C',
        clinic: 'B2C_Clinic',
        patient: {
          birth: moment().add(-age, 'years').format('YYYY-MM-DD'),
          gender,
          patientName: '홍길동',
        },
      });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingForB2CForm(false);
    }
  }, []);

  const moveToB2CResult = useCallback(async ({ id, version, d }) => {
    try {
      setIsLoadingForB2CResult(true);
      const keys = Object.keys(d);
      const survey = keys
        .filter((k: string) => codeMap.has(k))
        .map((k: string) => {
          const v = d[k];
          const r = {
            code: k,
            value: v,
            desc: '',
          };
          if (_.isArray(v) && v.includes(-1)) {
            // 주관식 응답값 -1 을 가지고 있다면
            r.desc = d?.[`${k}Desc`] ?? '';
          } else {
            // @ts-ignore
            delete r.desc;
          }
          return r;
        });
      const bResult = await getByeonjeungResult({ sCodes: extractScodes(JSON.stringify(survey)) });
      await updateSurveyAPI({
        id,
        _version: version,
        byeonjeungData: JSON.stringify(bResult.팔강),
        surveyType: 'B2C',
        rawData: JSON.stringify(d),
        completedDt: Math.floor(Date.now() / 1000),
      });
      if (mbtiFlowFormData?.uuid) {
        markMBTIUserCompletedAPI(mbtiFlowFormData.uuid, id).catch(() => {});
      }
      setPageIndex(Page.B2CResult);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingForB2CResult(false);
    }
  }, [mbtiFlowFormData?.uuid]);

  const InsuranceTypeFlag = surveyType === 'INSURANCE';

  async function onFooterClick(mode: 'prev' | 'next' = 'next') {
    switch (pageIndex) {
      case Page.MainSymptom: // 주소증 텍스트 입력 화면
        if (mode === 'prev') {
          setPageIndex(Page.MainCategory);
        } else {
          const filteredRequiredSurveys = _.without(
            _.filter(requiredSurveys, (k: string) => mainSurveyList.includes(k)),
            ...(omitReqSurveyCdByIsrcSurveyCd.get(selectedMainSurveyCode) ?? []),
          );
          const newSurveyList = _.uniq([...surveyList, ...filteredRequiredSurveys]);
          if (selectedMainSurveyCode === 0) {
            const cSurvey = newSurveyList[0];
            const exceptYnSurveysFlag = noYnSurveys.includes(cSurvey) && !requiredYnSurveys.includes(cSurvey);
            setFormData({
              currentSurvey: exceptYnSurveysFlag
                ? getSurveyObj(cSurvey)?.answers?.find((e) => e?.value)?.next?.[0]
                : cSurvey,
              nextSurvey: exceptYnSurveysFlag
                ? getSurveyObj(cSurvey)
                    ?.answers?.find((e) => e?.value)
                    ?.next?.slice(1)
                : [],
              surveyList: newSurveyList,
            });
          } else {
            const exceptYnSurveysFlag =
              !noYnSurveys.includes(selectedMainSurveyCode) &&
              !requiredYnSurveys.includes(selectedMainSurveyCode);
            setFormData({
              currentSurvey: exceptYnSurveysFlag
                ? getSurveyObj(selectedMainSurveyCode)?.answers?.find((e) => e?.value)?.next?.[0]
                : selectedMainSurveyCode,
              nextSurvey: exceptYnSurveysFlag
                ? getSurveyObj(selectedMainSurveyCode)
                    ?.answers?.find((e) => e?.value)
                    ?.next?.slice(1)
                : [],
              surveyList: newSurveyList,
            });
          }

          surveyUpdate({ id: _id, mainSymptom, rawData: JSON.stringify(formData), _version });
          setPageIndex(Page.DetailSurvey);
        }
        break;
      case Page.Term:
        if (mode === 'next') {
          surveyUpdate({
            id: _id,
            _version,
            termAgreement: {
              personalInfo: personalTermAgreement,
              service: serviceTermAgreement,
            },
            rawData: JSON.stringify(formData),
          });
          if (InsuranceTypeFlag) {
            // 첩약건보를 선택가능한 설문 모드
            setPageIndex(Page.ModeSelect); // 첩약건보 설문 화면
          } else {
            setPageIndex(Page.MainCategory); // 첩약건보 설문 화면 건너뛰고 주소증
          }
        }
        break;
      case Page.ModeSelect:
        if (mode === 'next') {
          if (surveyMode === 'GENERAL') {
            setPageIndex(Page.MainCategory);
          } else {
            const filteredRequiredSurveys = _.without(
              _.filter(requiredSurveys, (k: string) => mainSurveyList.includes(k)),
              ...(omitReqSurveyCdByIsrcSurveyCd.get(selectedMainSurveyCode) ?? []),
            );
            const newSurveyList = _.uniq([...surveyList, ...filteredRequiredSurveys]);
            const exceptYnSurveysFlag =
              !noYnSurveys.includes(selectedMainSurveyCode) &&
              !requiredYnSurveys.includes(selectedMainSurveyCode);
            setFormData({
              currentSurvey: exceptYnSurveysFlag
                ? getSurveyObj(selectedMainSurveyCode)?.answers?.find((e) => e?.value)?.next?.[0]
                : selectedMainSurveyCode,
              nextSurvey: exceptYnSurveysFlag
                ? getSurveyObj(selectedMainSurveyCode)
                    ?.answers?.find((e) => e?.value)
                    ?.next?.slice(1)
                : [],
              surveyList: newSurveyList,
            });
            surveyUpdate({
              id: _id,
              _version,
              mode: surveyMode,
              mainSurveyCode: String(selectedMainSurveyCode),
              rawData: JSON.stringify(formData),
            });
            setPageIndex(Page.DetailSurvey);
          }
        }
        break;
      case Page.MainCategory:
        if (mode === 'prev') {
          setPageIndex(Page.ModeSelect); // 이전 버튼은 첩약건보 모드에서만 노출
        } else if (mode === 'next') {
          surveyUpdate({
            id: _id,
            _version,
            mainSurveyCode: String(selectedMainSurveyCode),
          });
          setPageIndex(pageIndex + 1);
        }
        break;
      case Page.Continue:
        if (mode === 'prev') {
          if (surveyType === 'B2C') {
            setPageIndex(Page.B2CMain);
          } else {
            setPageIndex(Page.Term);
          }
          setFormData({
            ..._.mapValues(formData, () => undefined),
            ...initFormData,
            _id,
            _version,
            visitedSurvey: [],
            mode: surveyMode,
            surveyType,
            mainSurveyList: initMainSurveyList,
            patient,
            surveyList,
          });
        } else if (formData?.visitedSurvey?.length) {
          setPageIndex(Page.DetailSurvey);
        } else if (mainSymptom || data?.mainSymptom) {
          setFormData({ mainSymptom: data?.mainSymptom, termAgreement: true });
          setPageIndex(Page.MainSymptom);
        }
        break;
      case Page.DetailSurvey:
        if (mode === 'next') {
          const { currentSurvey, nextSurvey = [], prevFlag = false } = formData;
          let nextSurveyCopy = nextSurvey;
          const next = formData[`${currentSurvey}Next`];
          /** next : 현 설문 응답에서 받아온, 앞으로 진행해야하는 설문 list
           * undefined(next값 자체가 존재하지 않는 경우,  + 설문 응답을 안한 경우는 footer 버튼 disabled로 막아야함)
           * 0 (아무 증상 없음, 아니오 같은 응답으로 설문을 건너 뛰어야 하는 경우)
           * 그 외엔 string[]으로 들어옴
           * */
          if (prevFlag) {
            nextSurveyCopy = nextSurvey.filter((e: string) => !e.startsWith(currentSurvey));
          }
          if (debugMode) {
            console.log(
              `currentSurvey: ${currentSurvey}`,
              `next: ${next}`,
              `nextSurvey: ${nextSurvey}`,
              `nextSurveyCopy: ${nextSurveyCopy}`,
              `surveyList: ${surveyList}`,
              `visitedSurvey: ${visitedSurvey}`,
            );
          }
          if (_.isArray(next)) {
            // 설문 응답에서 다음 설문 값이 유의미하게 설정된 경우
            if (nextSurveyCopy.length) {
              // current 설문 이전에 세팅된 다음 설문 리스트가 남은 경우
              const body = {
                // current 설문에서 새로이 받아온 다음 설문(next)를 우선으로 진행
                currentSurvey: next[0],
                nextSurvey: _.uniq([...next.slice(1), ...nextSurveyCopy]),
                prevFlag: false,
              };
              surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
              if (debugMode) {
                console.log(body);
              }
              setFormData(body);
            } else {
              // current 설문 이전에 세팅된 다음 설문 리스트가 없는 경우 (= next가 유일한 다음 설문 단서일 때)
              const body = {
                currentSurvey: next[0],
                nextSurvey: next.slice(1),
                prevFlag: false,
              };
              if (debugMode) {
                console.log(body);
              }
              surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
              setFormData(body);
            }
          } else if (next === 0 || next === undefined) {
            // 설문을 건너뛰는 응답을 한 경우 or next가 없는 설문이었던 경우
            if (nextSurveyCopy.length) {
              // 진행해야할 설문이 남아 있는 경우
              const body = {
                // 문제 없이 그대로 진행
                currentSurvey: nextSurveyCopy[0],
                nextSurvey: nextSurveyCopy.slice(1),
                prevFlag: false,
              };
              if (debugMode) {
                console.log(body);
              }
              surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
              setFormData(body);
            } else {
              // 진행할 설문도 없고 건너 뛰는 경우
              let depth = currentSurvey.length;
              let flag = true;
              while (depth > 1) {
                const parentsNext = formData[`${currentSurvey.slice(0, depth - 1)}Next`];
                const elem = currentSurvey.slice(0, depth);
                if (_.isArray(parentsNext) && parentsNext?.includes(elem)) {
                  if (!(_.last(parentsNext) === elem)) {
                    const index = parentsNext.findIndex((e) => e === elem);
                    const body = {
                      currentSurvey: parentsNext[index + 1],
                      nextSurvey: [...parentsNext.slice(index + 2)],
                      prevFlag: false,
                    };
                    if (debugMode) {
                      console.log(body);
                    }
                    surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
                    setFormData(body);
                    flag = false;
                    break;
                  }
                }
                depth -= 1;
              }

              if (flag) {
                const currentMainSurveyCode = currentSurvey.slice(0, 1);
                const nextIndex = surveyList.findIndex((e: string) => e === currentMainSurveyCode) + 1;
                const n = surveyList[nextIndex];
                const exceptYnSurveysFlag =
                  [...exceptYnSurveys, selectedMainSurveyCode].includes(n) &&
                  !noYnSurveys.includes(n) &&
                  !requiredYnSurveys.includes(n);
                const body = {
                  currentSurvey: exceptYnSurveysFlag
                    ? getSurveyObj(n)?.answers?.find((e) => e?.value)?.next?.[0]
                    : n,
                  nextSurvey: exceptYnSurveysFlag
                    ? getSurveyObj(n)
                        ?.answers?.find((e) => e?.value)
                        ?.next?.slice(1)
                    : [],
                  prevFlag: false,
                };
                if (debugMode) {
                  console.log(body);
                }
                setFormData(body);

                if (currentMainSurveyCode === selectedMainSurveyCode) {
                  // 주증상 선택 질문 이후에는 필수설문 진행 이전 간지 페이지로 이동
                  setPageIndex(Page.CheerUp);
                }

                const requiredStepSurveys = [
                  selectedMainSurveyCode,
                  ..._.without(
                    _.filter(requiredSurveys, (k: string) => mainSurveyList.includes(k)),
                    ...(omitReqSurveyCdByIsrcSurveyCd.get(selectedMainSurveyCode) ?? []),
                  ),
                ];
                if (requiredStepSurveys.includes(currentMainSurveyCode) && !requiredStepSurveys.includes(n)) {
                  // 필수 설문만 완료한 상태,
                  if (InsuranceTypeFlag && surveyMode !== 'GENERAL') {
                    // 첩약건보를 선택가능한 설문 유형이고, 환자가 첩약건보 질환 설문을 진행한 경우, 선택설문을 건너뛴다.
                    setPageIndex(Page.Confirm);
                  } else {
                    // 보험 설문이 아닐 경우 선택 설문으로 넘어간다.
                    setPageIndex(Page.OptionalSurveys);
                  }
                  surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
                } else if (nextIndex === surveyList.length) {
                  if (surveyType === 'B2C') {
                    setPageIndex(Page.B2CAdditionalSurvey);
                  } else {
                    setPageIndex(Page.Confirm);
                  }
                  surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
                }
              }
            }
          } else {
            if (debugMode) {
              console.log(
                'unexpected',
                `currentSurvey: ${currentSurvey}`,
                `next: ${next}`,
                `nextSurvey: ${nextSurvey}`,
              );
            }
          }
        } else if (mode === 'prev') {
          const { currentSurvey } = formData;
          const surveyListWithoutCur = visitedSurvey.filter((e: string) => e !== currentSurvey);
          const prevSurvey: any = _.last(surveyListWithoutCur);
          if (surveyListWithoutCur.length === 0) {
            // B2C는 prev 시 currentSurvey를 첫 설문 코드로 복원해야 함.
            // UUID flow는 B2CForm을 거치지 않아 useBody의 updatedData effect가 재실행되지 않으므로
            // currentSurvey를 undefined로 두면 재진입 시 빈 DetailSurvey 렌더됨.
            const restoredCurrentSurvey =
              surveyType === 'B2C' ? mainSurveyList?.[0] : prevSurvey;
            const body = {
              currentSurvey: restoredCurrentSurvey,
              visitedSurvey: surveyListWithoutCur,
            };
            setFormData(body);
            if (InsuranceTypeFlag && surveyMode !== 'GENERAL') {
              setPageIndex(Page.ModeSelect);
            } else if (surveyType === 'B2C') {
              // UUID flow는 정보 입력 페이지(B2CForm)가 없으므로 시작 화면(B2CMain)으로.
              setPageIndex(mbtiFlowFormData?.uuid ? Page.B2CMain : Page.B2CForm);
            } else {
              setPageIndex(Page.MainSymptom);
            }
          } else {
            const body = {
              currentSurvey: prevSurvey,
              visitedSurvey: surveyListWithoutCur,
              nextSurvey: [],
              prevFlag: true,
            };
            if (debugMode) {
              console.log(body);
            }
            setFormData(body);
            if (
              currentSurvey.startsWith(selectedOptionalSurveyList?.[0]) &&
              !prevSurvey.startsWith(selectedOptionalSurveyList?.[0])
            ) {
              setPageIndex(Page.OptionalSurveys);
            }
          }
        }
        break;
      case Page.CheerUp:
        if (mode === 'next') {
          const body = {
            prevFlag: false,
          };
          if (debugMode) {
            console.log(body);
          }
          setFormData(body);
        } else {
          const { currentSurvey } = formData;
          const surveyListWithoutCur = visitedSurvey.filter((e: string) => e !== currentSurvey);

          if (surveyListWithoutCur.length === 0) {
            setPageIndex(Page.MainSymptom);
          } else {
            const prevSurvey = _.last(surveyListWithoutCur);
            const body = {
              currentSurvey: prevSurvey,
              visitedSurvey: surveyListWithoutCur,
              prevFlag: true,
            };
            if (debugMode) {
              console.log(body);
            }
            setFormData(body);
          }
        }
        setPageIndex(Page.DetailSurvey);
        break;
      case Page.OptionalSurveys:
        if (mode === 'next') {
          if (!_.isEmpty(selectedOptionalSurveyList)) {
            const n = selectedOptionalSurveyList[0];
            const body = {
              currentSurvey: getSurveyObj(n)?.answers?.find((e) => e?.value)?.next?.[0],
              nextSurvey: getSurveyObj(n)
                ?.answers?.find((e) => e?.value)
                ?.next?.slice(1),
              prevFlag: false,
            };
            if (debugMode) {
              console.log(body);
            }
            setFormData(body);
            setPageIndex(Page.DetailSurvey);
          } else {
            if (surveyType === 'B2C') {
              setPageIndex(Page.B2CAdditionalSurvey);
              surveyUpdate({ id: _id, _version, rawData: JSON.stringify(formData) });
            } else {
              // 선택설문 없음 항목으로 진행 시, 바로 설문 완료 화면으로 넘어감
              await surveyUpdate({
                id: _id,
                _version,
                rawData: JSON.stringify(formData),
              });
              setPageIndex(Page.Confirm);
            }
          }
        } else {
          const body = {
            currentSurvey: _.last(visitedSurvey),
            nextSurvey: [],
            prevFlag: true,
          };
          if (debugMode) {
            console.log(body);
          }
          setFormData(body);
          setPageIndex(Page.DetailSurvey);
        }

        break;
      case Page.Review:
        if (mode === 'next') {
          surveyUpdate({ id: _id, _version, reviewRating, reviewComment, reviewFlag: true });
          setPageIndex(Page.End);
          break;
        } else {
          setPageIndex(Page.Confirm);
          break;
        }
      case Page.Confirm:
        if (mode === 'next') {
          surveyUpdate({
            id: _id,
            _version,
            rawData: JSON.stringify(formData),
            completedDt: Number(moment().format('X')),
            mode: surveyMode,
          });
          setPageIndex(Page.Review);
          break;
        } else {
          if (_.isEmpty(selectedOptionalSurveyList) && !(InsuranceTypeFlag && surveyMode !== 'GENERAL')) {
            // 첩약건보 질환 유형의 설문이 아니면서 선택 설문을 넘겼던 경우
            setPageIndex(Page.OptionalSurveys);
            break;
          } else {
            const surveyListWithoutLast = visitedSurvey.filter((e: string) => e !== _.last(visitedSurvey));

            const body = {
              currentSurvey: _.last(visitedSurvey),
              visitedSurvey: surveyListWithoutLast,
              prevFlag: true,
            };
            if (debugMode) {
              console.log(body);
            }
            setPageIndex(Page.DetailSurvey);
            setFormData(body);
            break;
          }
        }
      case Page.ServiceTerm:
      case Page.PersonalInfoTerm:
        if (mode === 'next') {
          setPageIndex(Page.Term);
        }
        break;
      case Page.B2CMain:
        if (mode === 'next') {
          setPageIndex(Page.B2CForm);
        }
        break;
      case Page.B2CForm:
        if (mode === 'next') {
          startB2CSurvey({
            paramsForRequest,
            d: formData,
            name: b2cFormData.name,
            age: b2cFormData.age,
            gender: b2cFormData.gender,
          });
        }
        break;
      case Page.B2CAdditionalSurvey:
        if (mode === 'next') {
          moveToB2CResult({
            id: _id,
            version: _version,
            d: formData,
          });
        } else {
          setPageIndex(Page.DetailSurvey);
        }
        break;
      default:
        if (mode === 'next') {
          setPageIndex(pageIndex + 1);
        } else {
          setPageIndex(pageIndex - 1);
        }
        break;
    }
  }

  useEffect(() => {
    const { currentSurvey } = formData;
    const value = formData[currentSurvey];
    const type = getSurveyObj(currentSurvey ?? 'a').selectType;
    if (!_.isEmpty(value) && !['string', 'number'].includes(typeof value)) {
      // 비어있지 않은 Object, Array Case
      switch (type) {
        case SelectType.Check:
          setIsDisabledForSurvey(false);
          break;
        case SelectType.Onset:
          if (_.has(value, 'onset')) {
            setIsDisabledForSurvey(false);
            break;
          }
          setIsDisabledForSurvey(true);
          break;
        case SelectType.Scale:
          if (_.has(value, 'scale')) {
            setIsDisabledForSurvey(false);
            break;
          }
          setIsDisabledForSurvey(true);
          break;
        default:
          setIsDisabledForSurvey(true);
          break;
      }
    } else if (['string', 'number'].includes(typeof value)) {
      setIsDisabledForSurvey(false);
    } else {
      setIsDisabledForSurvey(true);
    }
  }, [formData.currentSurvey, formData[formData.currentSurvey]]);

  useEffect(() => {
    if (formData.reviewRating) {
      setIsDisabledForReview(false);
    } else {
      setIsDisabledForReview(true);
    }
  }, [formData.reviewRating]);

  useEffect(() => {
    if (b2cFormData.gender && b2cFormData.age > -1 && b2cFormData.name) {
      setIsDisabledForB2CForm(false);
    } else {
      setIsDisabledForB2CForm(true);
    }
  }, [b2cFormData.gender, b2cFormData.age, b2cFormData.name]);

  useEffect(() => {
    if (!_.isEmpty(formData['a-s-pl']) && !_.isEmpty(formData['a-s-hy']) && !_.isEmpty(formData['a-s-hs'])) {
      setIsDisabledForB2CAddintionalSurvey(false);
    } else {
      setIsDisabledForB2CAddintionalSurvey(true);
    }
  }, [formData['a-s-pl'], formData['a-s-hy'], formData['a-s-hs']]);

  useEffect(() => {
    if (formData.additionalSymptomFlag !== undefined) {
      setIsDisabledForConfirm(false);
    } else {
      setIsDisabledForConfirm(true);
    }
  }, [formData.additionalSymptomFlag]);

  useEffect(() => {
    if (formData.selectedMainSurveyCode || formData.selectedMainSurveyCode === 0) {
      setIsDisabledForMainCategory(false);
    } else {
      setIsDisabledForMainCategory(true);
    }
  }, [formData.selectedMainSurveyCode]);

  useEffect(() => {
    if (!_.isEmpty(selectedOptionalSurveyList) || optionalSurveysSkipFlag) {
      setIsDisabledForOptionalSurvey(false);
    } else {
      setIsDisabledForOptionalSurvey(true);
    }
  }, [selectedOptionalSurveyList, optionalSurveysSkipFlag]);

  useEffect(() => {
    if (pageIndex === Page.Term) {
      if (personalTermAgreement && serviceTermAgreement) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    }
  }, [pageIndex, personalTermAgreement, serviceTermAgreement]);

  useEffect(() => {
    if (requestSuccess) {
      if (debugMode) {
        console.log(updatedData);
      }
      setParamsForRequest({ ...paramsForRequest, completedDt: undefined });
      setFormData({ _version: updatedData._version });
    }
  }, [requestSuccess]);

  useEffect(() => {
    if (error) {
      setParamsForRequest({ ...paramsForRequest, completedDt: undefined });
    }
  }, [error]);

  return (
    <Row style={{ display: 'flex', width: '100%', position: 'relative' }}>
      {[
        Page.DetailSurvey,
        Page.MainSymptom,
        Page.MainCategory,
        Page.CheerUp,
        Page.OptionalSurveys,
        Page.Review,
        Page.Confirm,
      ].includes(pageIndex) && (
        <div
          style={{
            position: 'absolute',
            top: '-1px',
            left: 0,
            width: 'calc(100% + 48px)',
            height: '1px',
            borderTop: '1px solid #f5f5f5',
            zIndex: 999,
            marginLeft: '-24px',
          }}
        />
      )}
      {pageIndex === Page.Term && (
        <Row yzTypeRow="footer-single-button">
          <Button
            disabled={isDisabled}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            {START}
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {[Page.B2CForm].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          <Button
            disabled={isDisabledForB2CForm}
            onClick={_.debounce(isLoadingForB2CForm ? () => {} : () => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            {isLoadingForB2CForm ? (
              <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Spinner />
              </div>
            ) : (
              <>
                <span>{NEXT_BUTTON}</span>
                <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
              </>
            )}
          </Button>
        </Row>
      )}
      {[Page.DetailSurvey, Page.OptionalSurveys].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          <Button onClick={_.debounce(() => onFooterClick('prev'), 100)} yzTypeButton="footer-prev-button">
            <Icon icon="prevVector" style={{ margin: '0 12px 2px 0', verticalAlign: 'middle' }} />
            {PREV_BUTTON}
          </Button>
          <Button
            disabled={pageIndex === Page.DetailSurvey ? isDisabledForSurvey : isDisabledForOptionalSurvey}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            <span>{NEXT_BUTTON}</span>
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {[Page.B2CAdditionalSurvey].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          {/* <Button onClick={_.debounce(() => onFooterClick('prev'), 100)} yzTypeButton="footer-prev-button">
            <Icon icon="prevVector" style={{ margin: '0 12px 2px 0', verticalAlign: 'middle' }} />
            {PREV_BUTTON}
          </Button> */}
          <Button
            disabled={isDisabledForB2CAddintionalSurvey}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            {isLoadingForB2CResult ? (
              <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Spinner />
              </div>
            ) : (
              <span>{NEXT_TO_CONFIRM_BUTTON}</span>
            )}
          </Button>
        </Row>
      )}
      {[Page.CheerUp, Page.MainSymptom].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          <Button onClick={_.debounce(() => onFooterClick('prev'), 100)} yzTypeButton="footer-prev-button">
            <Icon icon="prevVector" style={{ margin: '0 12px 2px 0', verticalAlign: 'middle' }} />
            {PREV_BUTTON}
          </Button>
          <Button onClick={_.debounce(() => onFooterClick('next'), 100)} yzTypeButton="footer-next-button">
            {NEXT_BUTTON}
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {pageIndex === Page.Review && (
        <Row yzTypeRow="footer-single-button">
          <Button
            disabled={isDisabledForReview}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            {REVIEW_NEXT_BUTTON}
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {[Page.MainCategory].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          {InsuranceTypeFlag && (
            <Button onClick={_.debounce(() => onFooterClick('prev'), 100)} yzTypeButton="footer-prev-button">
              <Icon icon="prevVector" style={{ margin: '0 12px 2px 0', verticalAlign: 'middle' }} />
              {PREV_BUTTON}
            </Button>
          )}
          <Button
            disabled={isDisabledForMainCategory}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            {NEXT_BUTTON}
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {[Page.ModeSelect].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          <Button
            disabled={selectedInsuranceSurveyCode === undefined}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            {MODE_SELECT_BUTTON}
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {pageIndex === Page.Confirm && (
        <Row yzTypeRow="footer-single-button">
          <Button onClick={_.debounce(() => onFooterClick('prev'), 100)} yzTypeButton="footer-prev-button">
            <Icon icon="prevVector" style={{ margin: '0 12px 2px 0', verticalAlign: 'middle' }} />
            {PREV_BUTTON}
          </Button>
          <Button
            disabled={isDisabledForConfirm}
            onClick={_.debounce(() => onFooterClick('next'), 100)}
            yzTypeButton="footer-next-button"
          >
            <span>{NEXT_BUTTON}</span>
            <Icon icon="vector" style={{ margin: '0 0 2px 12px', verticalAlign: 'middle' }} />
          </Button>
        </Row>
      )}
      {pageIndex === Page.Continue && (
        <Row yzTypeRow="footer-single-button">
          <Button onClick={_.debounce(() => onFooterClick('prev'), 100)} yzTypeButton="footer-prev-button">
            {INIT_SURVEY_BUTTON}
          </Button>
          <Button onClick={_.debounce(() => onFooterClick('next'), 100)} yzTypeButton="footer-next-button">
            {CONTINUE_SURVEY_BUTTON}
          </Button>
        </Row>
      )}
      {[Page.ServiceTerm, Page.PersonalInfoTerm].includes(pageIndex) && (
        <Row yzTypeRow="footer-single-button">
          <Button onClick={_.debounce(() => onFooterClick('next'), 100)} yzTypeButton="footer-next-button">
            {CONFIRM}
          </Button>
        </Row>
      )}
    </Row>
  );
}
